import React, { useCallback, useState, useEffect } from 'react';
import {
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Box,
  Typography,
  Chip
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon
} from '@material-ui/icons';

const RejectionListComponent = ({ rejectionList, setFieldValue }) => {
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingText, setEditingText] = useState('');

  // Función para ordenar la lista con "Otro" al final
  const sortListItems = (list) => {
    const otherItems = list.filter(item => item !== 'Otro');
    const hasOther = list.includes('Otro');
    return hasOther ? [...otherItems, 'Otro'] : [...otherItems, 'Otro'];
  };

  // Asegurar que "Otro" siempre esté en la lista y al final
  useEffect(() => {
    const sortedList = sortListItems(rejectionList);
    // Solo actualizar si la lista cambió
    if (JSON.stringify(sortedList) !== JSON.stringify(rejectionList)) {
      setFieldValue('rejectionList', sortedList);
    }
  }, [rejectionList, setFieldValue]);

  const addItem = useCallback(() => {
    if (newItem.trim()) {
      // Verificar que no sea duplicado
      if (!rejectionList.includes(newItem.trim())) {
        // Filtrar "Otro" temporalmente, agregar el nuevo elemento, y luego poner "Otro" al final
        const otherItems = rejectionList.filter(item => item !== 'Otro');
        const updatedList = [...otherItems, newItem.trim(), 'Otro'];
        setFieldValue('rejectionList', updatedList);
        setNewItem('');
      }
    }
  }, [newItem, rejectionList, setFieldValue]);

  const deleteItem = useCallback(
    (index) => {
      const itemToDelete = rejectionList[index];
      // No permitir eliminar "Otro"
      if (itemToDelete === 'Otro') {
        return;
      }
      const updatedList = rejectionList.filter((_, i) => i !== index);
      // Asegurar que "Otro" esté al final
      const sortedList = sortListItems(updatedList);
      setFieldValue("rejectionList", sortedList);
    },
    [rejectionList, setFieldValue]
  );

  const startEdit = (index) => {
    const itemToEdit = rejectionList[index];
    // No permitir editar "Otro"
    if (itemToEdit === 'Otro') {
      return;
    }
    setEditingIndex(index);
    setEditingText(rejectionList[index]);
  };

  const saveEdit = () => {
    if (editingText.trim()) {
      // Verificar que no sea duplicado (excepto consigo mismo)
      const isDuplicate = rejectionList.some((item, index) => 
        item === editingText.trim() && index !== editingIndex
      );
      
      if (!isDuplicate) {
        const updatedList = [...rejectionList];
        updatedList[editingIndex] = editingText.trim();
        // Asegurar que "Otro" esté al final después de la edición
        const sortedList = sortListItems(updatedList);
        setFieldValue('rejectionList', sortedList);
      }
    }
    setEditingIndex(-1);
    setEditingText('');
  };

  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingText('');
  };

  const isProtectedItem = (item) => item === 'Otro';

  return (
    <Paper style={{ padding: 16 }}>
      {/* Agregar nuevo elemento */}
      <Box display="flex" gap={1} marginBottom={2}>
        <TextField
          fullWidth
          size="small"
          label="Agregar elemento de rechazo"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addItem();
            }
          }}
          helperText="Nota: 'Otro' es un elemento permanente y siempre aparece al final"
        />
        <Button
          variant="contained"
          color="info"
          style={{ 
            backgroundColor: '#6ab3d4', 
            color: '#fff',  
            height: '37px',
            alignSelf: 'center'
          }}
          startIcon={<AddIcon />}
          onClick={addItem}
          disabled={!newItem.trim() || rejectionList.includes(newItem.trim())}
        >
          Agregar
        </Button>
      </Box>

      {/* Lista de elementos */}
      {rejectionList.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 16 }}>
          No hay elementos en la lista de rechazo
        </Typography>
      ) : (
        <List>
          {rejectionList.map((item, index) => (
            <ListItem key={index} divider>
              {editingIndex === index ? (
                // Modo edición
                <Box display="flex" alignItems="center" width="100%" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        saveEdit();
                      }
                      if (e.key === 'Escape') {
                        cancelEdit();
                      }
                    }}
                    autoFocus
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    style={{ backgroundColor: '#6ab3d4', color: '#fff' }}
                    onClick={saveEdit}
                    disabled={!editingText.trim() || 
                      (rejectionList.includes(editingText.trim()) && 
                       rejectionList[editingIndex] !== editingText.trim())}
                  >
                    <SaveIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={cancelEdit}
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                // Modo visualización
                <>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} sx={{ paddingLeft: '16px' }}>
                        {item}
                        {isProtectedItem(item) && (
                          <Chip 
                            icon={<LockIcon />}
                            label="Permanente" 
                            size="small" 
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={''}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      color="primary"
                      style={{ 
                        marginRight: 8,
                        opacity: isProtectedItem(item) ? 0.5 : 1
                      }}
                      onClick={() => startEdit(index)}
                      disabled={isProtectedItem(item)}
                      title={isProtectedItem(item) ? "Este elemento no se puede editar" : "Editar elemento"}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      color="secondary"
                      style={{ 
                        opacity: isProtectedItem(item) ? 0.5 : 1
                      }}
                      onClick={() => deleteItem(index)}
                      disabled={isProtectedItem(item)}
                      title={isProtectedItem(item) ? "Este elemento no se puede eliminar" : "Eliminar elemento"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default RejectionListComponent;